import React from 'react';
import { Badge, Box, Flex, Text } from '@chakra-ui/react';
import { CheckCircle, LockSimple, WarningCircle, XCircle } from 'phosphor-react';

import type { IExtratoVeredito } from '../../../../../../hooks/useContaCilExtratos';

type Nivel = 'ok' | 'aviso' | 'erro' | string;

const NIVEL_UI: Record<string, { cor: string; bg: string; label: string }> = {
  ok: { cor: '#059669', bg: 'rgba(16,185,129,0.12)', label: 'Confiável' },
  aviso: { cor: '#B45309', bg: 'rgba(245,158,11,0.14)', label: 'Revisar' },
  erro: { cor: '#DC2626', bg: 'rgba(239,68,68,0.12)', label: 'Inválido' },
  // compat com o nivel antigo 'warn'
  warn: { cor: '#B45309', bg: 'rgba(245,158,11,0.14)', label: 'Revisar' },
};

function ui(nivel?: Nivel) {
  return NIVEL_UI[nivel ?? 'ok'] ?? NIVEL_UI.aviso;
}

/** Selo compacto de confiabilidade do extrato (card/listagem). */
export function VeredictoBadge({
  veredito,
  retido,
}: {
  veredito?: IExtratoVeredito | null;
  retido?: boolean;
}) {
  if (!veredito?.nivel) return null;
  const u = ui(veredito.nivel);
  return (
    <Flex align="center" gap={1} flexShrink={0}>
      {retido && (
        <Badge
          colorScheme="yellow"
          borderRadius="full"
          display="flex"
          alignItems="center"
          gap={1}
          px={2}
        >
          <LockSimple size={10} weight="bold" /> retido
        </Badge>
      )}
      <Badge borderRadius="full" px={2} bg={u.bg} color={u.cor} title={`Auditor: ${u.label}`}>
        {u.label}
      </Badge>
    </Flex>
  );
}

function IconeCheck({ nivel, ok }: { nivel: string; ok: boolean }) {
  if (ok)
    return (
      <Box color="#10B981">
        <CheckCircle size={15} weight="fill" />
      </Box>
    );
  if (nivel === 'erro')
    return (
      <Box color="#EF4444">
        <XCircle size={15} weight="fill" />
      </Box>
    );
  return (
    <Box color="#F59E0B">
      <WarningCircle size={15} weight="fill" />
    </Box>
  );
}

/** Checklist completo do veredito (modal de detalhe). */
export function VeredictoChecklist({ veredito }: { veredito?: IExtratoVeredito | null }) {
  if (!veredito?.checks?.length) {
    // fallback: critica antiga so com msg
    if (veredito?.msg) {
      const u = ui(veredito.nivel);
      return (
        <Flex
          align="flex-start"
          gap={2}
          bg={u.bg}
          color={u.cor}
          borderRadius="xl"
          p={3}
          fontSize="13px"
        >
          <Box mt="2px" flexShrink={0}>
            <WarningCircle size={16} weight="fill" />
          </Box>
          <Text>{veredito.msg}</Text>
        </Flex>
      );
    }
    return null;
  }
  const u = ui(veredito.nivel);
  return (
    <Box
      borderWidth="1px"
      borderColor="rgba(15,23,42,0.06)"
      borderRadius="xl"
      overflow="hidden"
      bg="rgba(255,255,255,0.5)"
    >
      <Flex align="center" justify="space-between" px={3} py={2} bg={u.bg}>
        <Flex align="center" gap={2}>
          <Text
            fontSize="12px"
            fontWeight="700"
            color={u.cor}
            textTransform="uppercase"
            letterSpacing="0.04em"
          >
            Auditoria do extrato · {u.label}
          </Text>
          {veredito.bloqueia && (
            <Badge colorScheme="yellow" borderRadius="full" fontSize="9px">
              retido
            </Badge>
          )}
        </Flex>
        <Text fontSize="11px" fontWeight="700" color={u.cor}>
          {Math.round((veredito.score ?? 0) * 100)}%
        </Text>
      </Flex>
      <Flex direction="column">
        {veredito.checks.map(c => (
          <Flex
            key={c.id}
            align="center"
            gap={2}
            px={3}
            py={2}
            borderTopWidth="1px"
            borderColor="rgba(15,23,42,0.04)"
          >
            <IconeCheck nivel={c.nivel} ok={c.ok} />
            <Text fontSize="13px" color="#0F172A" fontWeight="600" flex={1} minW={0} isTruncated>
              {c.label}
            </Text>
            {c.detalhe && (
              <Text fontSize="11px" color="#64748B" isTruncated maxW="50%" title={c.detalhe}>
                {c.detalhe}
              </Text>
            )}
          </Flex>
        ))}
      </Flex>
    </Box>
  );
}
